// Which tracked asset (a drive) a flat-fee charge sells — migration 0058.
//
// "Production Media — USB Hard Drive (<2 TB)" is billed as a flat-fee time
// entry; picking the drive here ties that sale to the drive's purchase
// expense, so its profit can be read off later. Internal only: the invoice
// still shows the media charge, never the drive's cost.
//
// Lists only AVAILABLE assets (no live entry sells or reserves them), plus the
// one this entry already sells — which the filter would otherwise hide when
// editing it. Hidden without asset.read, since the list would 403.

import { useAssets } from '../api/queries/assets';
import { can } from '../auth/store';
import { assetOptionLabel } from '../utils/asset';

export interface SoldAssetPickerProps {
  value: number | null;
  onChange: (assetId: number | null) => void;
  /** The asset this entry already sells, when editing one. */
  current?: { id: number; name: string } | null;
  disabled?: boolean;
  error?: string;
  /** Wrapper class, so each host form keeps its own field styling. */
  className?: string;
  id?: string;
}

export function SoldAssetPicker(props: SoldAssetPickerProps): JSX.Element | null {
  const { value, onChange, current, disabled = false, error, className, id = 'sold-asset' } = props;
  const allowed = can('asset.read');
  // Not even fetched without asset.read — it would only 403.
  const q = useAssets({ available: true }, { enabled: allowed });
  if (!allowed) return null;

  const available = q.data?.assets ?? [];
  const options = current && !available.some((a) => a.id === current.id)
    ? [{ id: current.id, label: current.name }, ...available.map(toOption)]
    : available.map(toOption);

  // A wrapping <label> with the caption as a direct <span> child — the shape
  // every host form's `.x-field > span` rule styles.
  return (
    <label className={className}>
      <span>Device sold (optional)</span>
      <select
        id={id}
        className="form-input"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
        disabled={disabled || q.isPending}
      >
        <option value="">{q.isPending ? 'Loading assets…' : '— None —'}</option>
        {options.map((o) => (
          <option key={o.id} value={o.id}>{o.label}</option>
        ))}
      </select>
      {error && <span className="field-error">{error}</span>}
    </label>
  );
}

function toOption(a: Parameters<typeof assetOptionLabel>[0] & { id: number }) {
  return { id: a.id, label: assetOptionLabel(a) };
}
